/**
 * Toast — notificación temporal de éxito o error
 */
import { useEffect, useState } from 'react'
import { CheckCircle2, XCircle, AlertTriangle, X } from 'lucide-react'
import { emitToast } from '@/lib/globalToast'

export type ToastType = 'success' | 'error' | 'warning'

export interface ToastMessage {
  id: number
  type: ToastType
  message: string
}

interface ToastProps {
  toasts: ToastMessage[]
  onDismiss: (id: number) => void
}

export function ToastContainer({ toasts, onDismiss }: ToastProps) {
  return (
    <div className="fixed bottom-5 right-5 z-[100] flex flex-col gap-2 pointer-events-none">
      {toasts.map(t => (
        <ToastItem key={t.id} toast={t} onDismiss={onDismiss} />
      ))}
    </div>
  )
}

function ToastItem({ toast, onDismiss }: { toast: ToastMessage; onDismiss: (id: number) => void }) {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    // Entrada
    const show = setTimeout(() => setVisible(true), 10)
    // Auto-dismiss: el éxito se confirma de un vistazo; un aviso o un error
    // («la factura quedó sin comprobante») hay que poder leerlo entero.
    const hide = setTimeout(() => {
      setVisible(false)
      setTimeout(() => onDismiss(toast.id), 300)
    }, toast.type === 'success' ? 3500 : 8000)
    return () => { clearTimeout(show); clearTimeout(hide) }
  }, [toast.id, toast.type, onDismiss])

  const palette =
    toast.type === 'success'
      ? 'bg-success-surface border-success-border text-success-content-strong'
      : toast.type === 'warning'
      ? 'bg-warning-surface border-warning-border text-warning-content-strong'
      : 'bg-danger-surface border-danger-border text-danger-content-strong'
  const Icon =
    toast.type === 'success' ? CheckCircle2 : toast.type === 'warning' ? AlertTriangle : XCircle
  const iconColor =
    toast.type === 'success' ? 'text-success-solid' : toast.type === 'warning' ? 'text-warning-solid' : 'text-danger-solid'

  return (
    <div
      className={`pointer-events-auto flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg border text-sm font-medium transition-all duration-300
        ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}
        ${palette}`}
    >
      <Icon size={16} className={`shrink-0 ${iconColor}`} />
      <span>{toast.message}</span>
      <button
        onClick={() => { setVisible(false); setTimeout(() => onDismiss(toast.id), 300) }}
        className="ml-1 text-current opacity-50 hover:opacity-100"
      >
        <X size={13} />
      </button>
    </div>
  )
}

/**
 * Hook para emitir toasts desde una pantalla.
 *
 * ⚠️ Antes guardaba los toasts en estado LOCAL y la pantalla tenía que dibujar
 * su propio `<ToastContainer toasts={toast.toasts} …/>`. 18 pantallas usaban el
 * hook sin dibujarlo (Invoice360, honorarios, documentos del trabajador,
 * configuración contable…), así que ~100 avisos nunca se vieron. Peor con los
 * errores: `toast.error` le avisa a la red global que el error «ya se mostró»,
 * y la red se callaba también — el error quedaba en silencio total.
 *
 * Ahora todo va al `GlobalToastHost` (montado una vez en `main.tsx`) vía
 * `emitToast`. `toasts` queda siempre vacío para que las pantallas que todavía
 * dibujan un `ToastContainer` propio no dupliquen el aviso; se pueden limpiar
 * de a poco.
 */
const SIN_TOASTS_LOCALES: ToastMessage[] = []
const NOOP_DISMISS = (_id: number) => {}
const TOAST_API = {
  toasts: SIN_TOASTS_LOCALES,
  dismiss: NOOP_DISMISS,
  success: (msg: string) => emitToast('success', msg),
  error: (msg: string) => emitToast('error', msg),
  warning: (msg: string) => emitToast('warning', msg),
}

export function useToast() {
  return TOAST_API
}
